import { type JSX,useCallback, useRef } from 'react';

import { Box,Stack, TextField } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { useDrawerContext } from 'common/components/DrawerContext/drawerContext';
import { EditDrawer } from 'common/components/EditDrawer';
import { setSaveButtonEnabled } from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { ReadOnlyTextField } from 'common/components/sharedCustomComponents/ReadOnlyTextField/ReadOnlyTextField';
import {
    APIRolePostParams,
    APIRolePutParams
} from 'common/services/api/Roles/Roles.api';
import {
    postRole,
    PostRoleThunkResponse,
    putRole,
    PutRoleThunkResponse
} from 'common/services/api/Roles/Roles.thunk';
import { EditDrawerContentProps, Operations } from 'common/types/TypeDefs';
import { editDrawerTextFieldStyles } from 'common/utils/StyleHelpers';

interface RoleItem {
    name: string;
}

interface RoleDrawerProperties {
    roleNameFieldRef: React.RefObject<HTMLInputElement | undefined>;
    tenantName: string;
    tenantId: string;
}

function useRoleDrawer(
    entityName: string,
    drawerProps: RoleDrawerProperties,
    editingItemInfo?: EditDrawerContentProps<RoleItem>['editingItemInfo']
) {
    const dispatch = useAppDispatch();

    const drawerContext = useDrawerContext<
        RoleItem,
        RoleDrawerProperties,
        APIRolePostParams,
        PostRoleThunkResponse,
        APIRolePutParams,
        PutRoleThunkResponse
    >(entityName, drawerProps, editingItemInfo);

    const handleSaveBtnEnabled = useCallback(() => {
        dispatch(setSaveButtonEnabled(!!drawerProps?.roleNameFieldRef?.current?.value));
    }, [dispatch, drawerProps?.roleNameFieldRef]);

    const getPostOptions = useCallback((): APIRolePostParams | null => ({
        name: drawerProps.roleNameFieldRef.current?.value ?? '',
        tenantId: drawerProps.tenantId,
    }), [drawerProps]);

    const getPutOptions = useCallback((): APIRolePutParams | null => ({
        name: drawerProps.roleNameFieldRef.current?.value ?? '',
        id: editingItemInfo?.editingItemId ?? '',
        tenantId: drawerProps.tenantId,
    }), [drawerProps, editingItemInfo?.editingItemId]);

    const createSuccessHandler = useCallback((postResult: PostRoleThunkResponse) => {
        drawerContext.createSuccessHandler(postResult);
        dispatch(setRows(postResult.rows));
    }, [drawerContext, dispatch]);

    const editSuccessHandler = useCallback((putResult: PutRoleThunkResponse) => {
        drawerContext.editSuccessHandler(putResult);
        dispatch(setRows(putResult.rows));
    }, [drawerContext, dispatch]);

    const postCallback = useCallback(() => {
        drawerContext.postCallback(postRole, getPostOptions, createSuccessHandler);
    }, [drawerContext, getPostOptions, createSuccessHandler]);

    const putCallback = useCallback(() => {
        drawerContext.putCallback(putRole, getPutOptions, editSuccessHandler);
    }, [drawerContext, getPutOptions, editSuccessHandler]);

    return {
        ...drawerContext,
        handleSaveBtnEnabled,
        postCallback,
        putCallback
    };
}

function CreateContent({
    entityName,
    processing,
    nameInConflict,
    handleSaveBtnEnabled,
    postCallback,
    drawerProps
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled: () => void;
    postCallback: () => void;
    drawerProps: RoleDrawerProperties;
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={postCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.roleNameFieldRef}
                    id="filled-basic"
                    size="small"
                    label="Role Name"
                    placeholder="Type Role Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Role name already in use
                </Box>
                }
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
            </Stack>
        </EditDrawer>
    );
}

function EditContent({
    entityName,
    processing,
    nameInConflict,
    handleSaveBtnEnabled,
    putCallback,
    drawerProps,
    editingItemInfo
}: Readonly<{
    entityName: string;
    processing: boolean;
    nameInConflict: boolean;
    handleSaveBtnEnabled: () => void;
    putCallback: () => void;
    drawerProps: RoleDrawerProperties;
    editingItemInfo?: EditDrawerContentProps<RoleItem>['editingItemInfo'];
}>): JSX.Element {
    return (
        <EditDrawer
            title={entityName}
            saveButtonFunction={putCallback}
            processing={processing}
        >
            <Stack spacing={2}>
                <TextField
                    sx={editDrawerTextFieldStyles(nameInConflict)}
                    inputRef={drawerProps.roleNameFieldRef}
                    defaultValue={editingItemInfo?.editingRow?.name}
                    id="filled-basic"
                    size="small"
                    label="Role Name"
                    placeholder="Type Role Name"
                    variant="filled"
                    focused
                    autoFocus={true}
                    onChange={handleSaveBtnEnabled}
                    InputProps={{
                        disableUnderline: nameInConflict
                    }}
                />
                {nameInConflict &&
                <Box component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}>
                    Role name already in use
                </Box>
                }
                <ReadOnlyTextField label="Tenant Name" value={drawerProps.tenantName} />
            </Stack>
        </EditDrawer>
    );
}

export function RolesCreateContent({
    title,
    editingItemInfo,
}: Readonly<EditDrawerContentProps<RoleItem>>) {
    const tenantDetails = useAppSelector(selectPageInfo);
    const roleDrawerProps: RoleDrawerProperties = {
        roleNameFieldRef: useRef<HTMLInputElement>(undefined),
        tenantId: tenantDetails?.id ?? '',
        tenantName: tenantDetails?.name ?? ''
    };

    const drawerState = useRoleDrawer(title, roleDrawerProps, editingItemInfo);

    if (drawerState.action === Operations.Create || drawerState.action === Operations.Link) {
        return (
            <CreateContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                postCallback={drawerState.postCallback}
                drawerProps={roleDrawerProps}
            />
        );
    } else if (drawerState.action === Operations.Edit) {
        if (!editingItemInfo) {
            return <></>;
        }
        return (
            <EditContent
                entityName={title}
                processing={drawerState.processing}
                nameInConflict={drawerState.nameInConflict}
                handleSaveBtnEnabled={drawerState.handleSaveBtnEnabled}
                putCallback={drawerState.putCallback}
                drawerProps={roleDrawerProps}
                editingItemInfo={editingItemInfo}
            />
        );
    }

    return <></>;
}
